// src/components/admin/SystemHealthWidget.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Update as UpdateIcon,
  Backup as BackupIcon,
  Monitor as MonitorIcon,
  NetworkCheck as NetworkIcon,
  Memory as MemoryIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const SystemHealthWidget = ({ data }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(timer);
  }, []);

  if (!data) return null;

  const getStatusColor = (status) => {
    const colors = {
      excellent: 'success',
      good: 'success',
      warning: 'warning',
      critical: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      excellent: <CheckCircleIcon />,
      good: <CheckCircleIcon />,
      warning: <WarningIcon />,
      critical: <ErrorIcon />
    };
    return icons[status] || <CheckCircleIcon />;
  };

  const getStatusText = (status) => {
    const texts = {
      excellent: 'Excelente',
      good: 'Bueno',
      warning: 'Advertencia',
      critical: 'Crítico'
    };
    return texts[status] || 'Desconocido';
  };

  const systemMetrics = [
    {
      name: 'Tiempo Actividad',
      value: `${data.uptime}%`,
      status: data.uptime >= 99.5 ? 'excellent' : data.uptime >= 98 ? 'good' : 'warning',
      icon: <MonitorIcon />,
      details: 'Disponibilidad del sistema'
    },
    {
      name: 'Tiempo Respuesta',
      value: `${data.responseTime}ms`,
      status: data.responseTime <= 200 ? 'excellent' : data.responseTime <= 500 ? 'good' : 'warning',
      icon: <SpeedIcon />,
      details: 'Velocidad promedio API'
    },
    {
      name: 'Errores 24h',
      value: data.errorsLast24h,
      status: data.errorsLast24h <= 5 ? 'excellent' : data.errorsLast24h <= 20 ? 'warning' : 'critical',
      icon: <ErrorIcon />,
      details: 'Errores registrados'
    }
  ];

  const systemServices = [
    {
      name: 'Base de Datos',
      status: 'excellent',
      uptime: 99.9,
      description: 'Firebase Firestore'
    },
    {
      name: 'Autenticación',
      status: 'excellent',
      uptime: 99.8,
      description: 'Firebase Auth'
    },
    {
      name: 'Almacenamiento',
      status: 'good',
      uptime: 99.2,
      description: 'Firebase Storage'
    },
    {
      name: 'Hosting',
      status: 'excellent',
      uptime: 99.9,
      description: 'Firebase Hosting'
    },
    {
      name: 'CDN',
      status: 'good',
      uptime: 98.7,
      description: 'Content Delivery'
    }
  ];

  const securityAlerts = [
    {
      type: 'info',
      message: 'Último backup completado exitosamente',
      time: data.lastBackup
    },
    {
      type: 'warning',
      message: 'Detectados 3 intentos de login fallidos',
      time: '2024-12-09 13:45:00'
    }
  ];

  const calculateOverallHealth = () => {
    let score = 0;
    systemMetrics.forEach(metric => {
      switch (metric.status) {
        case 'excellent':
          score += 100;
          break;
        case 'good':
          score += 80;
          break;
        case 'warning':
          score += 60;
          break;
        case 'critical':
          score += 20;
          break;
        default:
          score += 50;
      }
    });
    return Math.round(score / systemMetrics.length);
  };

  const overallHealth = calculateOverallHealth();
  const healthStatus = overallHealth >= 90 ? 'excellent' : overallHealth >= 70 ? 'good' : overallHealth >= 50 ? 'warning' : 'critical';

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="🔧 Salud del Sistema"
        subheader={`Última actualización: ${currentTime.toLocaleTimeString('es-ES')}`}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={getStatusText(healthStatus)}
              color={getStatusColor(healthStatus)}
              icon={getStatusIcon(healthStatus)}
              size="small"
            />
            <Tooltip title="Actualizar métricas">
              <IconButton size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      <CardContent>
        {/* Salud general del sistema */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3, 
            textAlign: 'center',
            bgcolor: `${getStatusColor(healthStatus)}.light`,
            border: 1,
            borderColor: `${getStatusColor(healthStatus)}.main`
          }}
        >
          <Typography variant="h3" fontWeight="bold" color={`${getStatusColor(healthStatus)}.dark`}>
            {overallHealth}%
          </Typography>
          <Typography variant="body1" color={`${getStatusColor(healthStatus)}.dark`}>
            Salud General del Sistema
          </Typography>
        </Paper>

        {/* Métricas principales */}
        <Typography variant="subtitle2" gutterBottom>
          📊 Métricas Principales
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {systemMetrics.map((metric, index) => (
            <Grid item xs={12} key={index}>
              <Box
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${getStatusColor(metric.status)}.main`,
                      width: 24,
                      height: 24
                    }}
                  >
                    {React.cloneElement(metric.icon, { fontSize: 'small' })}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {metric.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {metric.details}
                    </Typography>
                  </Box>
                </Box>
                
                <Box textAlign="right">
                  <Typography variant="body2" fontWeight="bold" color={`${getStatusColor(metric.status)}.main`}>
                    {metric.value}
                  </Typography>
                  <Chip
                    label={getStatusText(metric.status)}
                    color={getStatusColor(metric.status)}
                    size="small"
                    sx={{ fontSize: '0.6rem', height: 16 }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Estado de servicios */}
        <Typography variant="subtitle2" gutterBottom>
          🔧 Estado de Servicios
        </Typography>
        
        <List dense sx={{ mb: 2 }}>
          {systemServices.map((service, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {getStatusIcon(service.status)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight="bold">
                      {service.name}
                    </Typography>
                    <Typography variant="caption" color={`${getStatusColor(service.status)}.main`}>
                      {service.uptime}%
                    </Typography>
                  </Box>
                }
                secondary={service.description}
              />
            </ListItem>
          ))}
        </List>

        {/* Alertas de seguridad */}
        <Typography variant="subtitle2" gutterBottom>
          🛡️ Alertas de Seguridad
        </Typography>
        
        {securityAlerts.map((alert, index) => (
          <Alert 
            key={index} 
            severity={alert.type} 
            sx={{ mb: 1, py: 0 }}
          >
            <Typography variant="body2">
              {alert.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(alert.time).toLocaleString('es-ES')}
            </Typography>
          </Alert>
        ))}

        {/* Acciones rápidas */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box 
                sx={{ 
                  p: 1, 
                  textAlign: 'center', 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => console.log('Iniciar backup')}
              >
                <BackupIcon color="primary" />
                <Typography variant="caption" display="block">
                  Backup
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box 
                sx={{ 
                  p: 1, 
                  textAlign: 'center', 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => console.log('Ver logs')}
              >
                <MonitorIcon color="secondary" />
                <Typography variant="caption" display="block">
                  Logs
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SystemHealthWidget;